import {
  Button,
  type ComboBoxOption,
  FormComboBox,
  FullScreenLoading,
  RadioCard,
  SkeletonSpinnerCentered,
  Text,
  TextLink,
} from '@civilgrid/ui/components';
import { isBillingTierEnabled } from '@civilgrid/utils/src/common/featureAccess';
import { useFeatureFlagEnabled } from '@posthog/react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useBlocker, useParams } from '@tanstack/react-router';
import type { MultiLineString, MultiPolygon } from 'geojson';
import { map } from 'lodash';
import { DateTime } from 'luxon';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAsync } from 'react-use';
import { useSnapshot } from 'valtio';
import { editProject as apiEditProject } from '~/api/projects2';
import authorizations, { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import { type GetCostOutput, getCost, ZERO_COST } from '~/common/billingUtils';
import { capturePosthogEvent, captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { getRequiredFieldsForBillingTier } from '~/common/projectUtils';
import { ORG_SWITCHER_CURRENT_PROJECT_KEY, ORG_SWITCHER_PROJECTS_KEY } from '~/common/queryKeys';
import { ProjectGeometryType } from '~/common/types';
import { Dropdown } from '~/components2/Dropdown';
import ControlledDatePicker from '~/components2/forms/controlled/ControlledDatePicker';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import ControlledTextArea from '~/components2/forms/controlled/ControlledTextArea';
import Form from '~/components2/forms/Form';
import Static from '~/components2/forms/Static';
import { getCircleDestCoords } from '~/components2/Map/canvas/circle/common';
import { Notify } from '~/components2/Notify';
import { SendEmailModal } from '~/components2/SendEmailModal';
import { calculateArea, calculateLength } from '~/helpers/unitHelpers';
import { useGetProject } from '~/hooks/useGetProject';
import { PROGRAM_MANAGER_FLAG, placeholderAndTooltips } from '~/pages/common/NewProject/common';
import { CompareProjectTypesDialog, CostBreakdownDialog } from '~/pages/common/ProjectTypeInfoDialogs';
import ProjectTypePill from '~/pages/common/ProjectTypePill';
import rootStore from '~/store';
import type { ProjectDetailsParams, TabDetailsProps } from '..';
import { type ProjectWithDetails, projectTypeOptions, type TFormValues } from './common';
import EditGeometryButton from './editGeometry/EditGeometryButton';
import MapRequestsAlert from './editGeometry/MapRequestsAlert';
import MoreButton from './MoreButton';
import { printProjectQuote } from './printQuote';
import Section from './Section';
import { trackProjectManagerSelfAssigned } from './trackProjectManagerSelfAssigned';
import UpdateBillingTierConfirmation from './UpdateBillingTierConfirmation';
import { upgradeBillingTierAndSaveEdits } from './upgradeBillingTier';

export function MapChildren() {
  return null;
}

export function TabDetails({ refetchProject }: TabDetailsProps) {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const projectRequest = useGetProject<ProjectWithDetails>({ projectId }, { refetchOnWindowFocus: false });
  const project = projectRequest.data;
  const queryClient = useQueryClient();
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [billingTierConfirmationDialog, setBillingTierConfirmationDialog] = useState<ReactNode | null>(null);
  const [quoteType, setQuoteType] = useState<'docx' | 'pdf' | null>(null);
  const { currentOrganization, currentOrganizationDetail, me } = useSnapshot(rootStore) as typeof rootStore;

  const { control, handleSubmit, setValue, trigger, formState, watch, reset } = useForm<TFormValues>();
  // Read at save time to tell an "Assign to me" click apart from picking yourself in the select.
  const assignedSelfViaButtonRef = useRef(false);
  const programManagerEnabled = useFeatureFlagEnabled(PROGRAM_MANAGER_FLAG);

  useBlocker({
    shouldBlockFn: () => {
      if (!formState.isDirty) return false;
      return !window.confirm('You have unsaved changes. Are you sure you want to leave?');
    },
  });

  const startDate = watch('startDate');
  const concStart = watch('constructionStartDate');
  const concEnd = watch('constructionEndDate');
  const projectManagerId = watch('projectManagerId');

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    trigger('constructionEndDate');
  }, [concStart, concEnd]);

  const setInitialValues = (p: ProjectWithDetails) => {
    setValue('scope', p.scope || '');
    setValue('name', p.name || '');
    setValue('projectManagerId', p.projectManager.id || '');
    setValue('programManagerId', p.programManager?.id || '');
    setValue('subProgramManagerId', p.subProgramManager?.id || '');
    setValue('type', p.type || '');
    setValue('detail', p.detail);
    setValue('projectStatus', p.projectStatus || '');
    setValue('projectPhase', p.projectPhase || '');
    setValue('startDate', p.startDate);
    setValue('endDate', p.endDate);
    setValue('constructionStartDate', p.constructionStartDate);
    setValue('constructionEndDate', p.constructionEndDate);
    setValue('budget', p.budget);
    setValue('billingCode', p.billingCode || '');
    setValue('billingCode2', p.billingCode2 || '');
    setValue('client', p.client || '');
    setValue('address2', {
      street: p.address2?.street || '',
      crossStreets: p.address2?.crossStreets || '',
      city: p.address2?.city || '',
      state: p.address2?.state || '',
      zipCode: p.address2?.zipCode || '',
    });
    setValue('projectVicinity', p.projectVicinity);
    setValue('billingTier', p.billingTier);
    setValue('peName', p.peName);
    setValue('peLicenseNumber', p.peLicenseNumber);
    setValue('peLicenseState', p.peLicenseState);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (project) {
      setInitialValues(project);
    }
  }, [project]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (formState.isDirty) {
      if (isSaveDisabled === false) {
        return;
      }
      setIsSaveDisabled(false);
    } else {
      if (isSaveDisabled === true) {
        return;
      }

      setIsSaveDisabled(true);
    }
  }, [watch()]);

  const { submitCount, isValid } = formState;
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (formState.isSubmitted && !formState.isValid) {
      Notify.error({ message: 'Complete required fields' });
    }
  }, [submitCount, isValid]);

  // "metadata opened" surface #3 (project info panel); fires once per projectId mount.
  useEffect(() => {
    if (!projectId) return;
    captureTypedPosthogEvent(POSTHOG_EVENTS.projectMetadataOpened, {
      project_id: projectId,
      organization_id: rootStore.currentOrganizationId || undefined,
      metadata_source: 'project_info',
    });
  }, [projectId]);

  // reset assignedSelfViaButtonRef when PM select changes manually
  useEffect(() => {
    if (projectManagerId !== me?.id) {
      assignedSelfViaButtonRef.current = false;
    }
  }, [projectManagerId, me?.id]);

  const projectTypeLabel = useMemo(() => {
    if (project) {
      if (project.geometryType === 'linear') {
        return 'Linear';
      } else if (project.geometryType === 'polygon') {
        return 'Polygon';
      }
    }
    return '';
  }, [project]);

  const getPMs = (): ComboBoxOption[] => {
    if (currentOrganizationDetail.isLoading) return [];

    const options: ComboBoxOption[] = currentOrganizationDetail.members
      .filter((m) => m.status === 'active')
      .map((m) => ({ value: m.user.id, label: m.user.name, description: m.user.email }));
    // Assigned managers the org member list omits (e.g. CivilGrid staff hidden from customers) still need a label.
    for (const manager of [project?.projectManager, project?.programManager, project?.subProgramManager]) {
      if (manager?.id && !options.some((o) => o.value === manager.id)) {
        options.push({ value: manager.id, label: manager.name, description: manager.email });
      }
    }
    return options;
  };

  const lengthAndArea = useMemo(() => {
    const zeroValue = {
      length: { unit: 'ft', value: 0 },
      area: { unit: 'ac', value: 0 },
    };

    if (!project) {
      return zeroValue;
    }
    if (project.geometryType === ProjectGeometryType.Linear) {
      const multiLine = project.linear!.geometry as MultiLineString;
      const length = calculateLength({
        unitSystem: 'imperial',
        unitType: 'miles',
        geometry: multiLine,
      });
      const area = calculateArea({
        unitSystem: 'imperial',
        geometry: project.geometry as MultiPolygon,
      });
      return { length, area };
    } else if (
      project.geometryType === ProjectGeometryType.Polygon ||
      project.geometryType === ProjectGeometryType.Small_3ac ||
      project.geometryType === ProjectGeometryType.Circle
    ) {
      return {
        ...zeroValue,
        area: calculateArea({
          unitSystem: 'imperial',
          geometry: project.geometry as MultiPolygon,
        }),
      };
    }

    return zeroValue;
  }, [project]);

  const projectCost = useAsync(async () => {
    const zeroValue: GetCostOutput = ZERO_COST;
    if (project) {
      if (project.geometryType === ProjectGeometryType.Circle && !!project.circle) {
        const circleMultiLine: GeoJSON.MultiLineString = {
          type: 'MultiLineString',
          coordinates: map(project.circle.geometry.coordinates, (p) => {
            const radius = project.circle!.radius.value!;
            const destCoords = getCircleDestCoords({
              lngLat: { lng: p[0], lat: p[1] },
              radius,
            }).geometry.coordinates;

            const center = p;
            return [center, destCoords];
          }),
        };

        return await getCost({
          projectType: project.geometryType,
          geometry: circleMultiLine,
          subscription: currentOrganization!.subscription,
        });
      } else {
        return await getCost({
          projectType: project.geometryType!,
          geometry: project.linear?.geometry || project.geometry,
          subscription: currentOrganization!.subscription,
        });
      }
    } else {
      return zeroValue;
    }
  }, [project, currentOrganization]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const billingTierSelector = useMemo(() => {
    if (project?.billingTier === 'projectPlus') {
      return null;
    }

    // Hide billing tiers the org has disabled (enabledBillingTiers override). The project's current
    // tier is always shown; only upgrade targets are gated. Grid columns track the visible count.
    const subscription = currentOrganization?.subscription;
    const colsClass: Record<number, string> = {
      1: 'grid-cols-1',
      2: 'grid-cols-2',
      3: 'grid-cols-3',
      4: 'grid-cols-4',
    };

    let options = null;
    if (project?.billingTier === 'draft') {
      const enProposal = isBillingTierEnabled('proposal', subscription);
      const enProject = isBillingTierEnabled('project', subscription);
      const enPlus = isBillingTierEnabled('projectPlus', subscription);
      const count = 1 + [enProposal, enProject, enPlus].filter(Boolean).length;
      options = (
        <RadioCard
          aria-label="Select project type"
          className={colsClass[count]}
          value={watch('billingTier')}
          onValueChange={(v) => setValue('billingTier', v, { shouldDirty: true })}
        >
          <ProjectTypePill billingTier="draft" label="Draft" data-testid="project-details-billing-tier-draft" />
          {enProposal && (
            <ProjectTypePill
              billingTier="proposal"
              label={'Project Lite'}
              cost={projectCost}
              data-testid="project-details-billing-tier-proposal"
            />
          )}
          {enProject && (
            <ProjectTypePill
              billingTier="project"
              label="Project"
              cost={projectCost}
              data-testid="project-details-billing-tier-project"
            />
          )}
          {enPlus && (
            <ProjectTypePill
              billingTier="projectPlus"
              label="Project Plus"
              cost={projectCost}
              data-testid="project-details-billing-tier-project-plus"
            />
          )}
        </RadioCard>
      );
    } else if (project?.billingTier === 'proposal') {
      const enProject = isBillingTierEnabled('project', subscription);
      const enPlus = isBillingTierEnabled('projectPlus', subscription);
      const count = 1 + [enProject, enPlus].filter(Boolean).length;
      options = (
        <RadioCard
          aria-label="Select project type"
          className={colsClass[count]}
          value={watch('billingTier')}
          onValueChange={(v) => setValue('billingTier', v, { shouldDirty: true })}
        >
          <ProjectTypePill
            billingTier="proposal"
            currentBillingTier={project.billingTier}
            label="Project Lite"
            zeroCostLabel=" "
            data-testid="project-details-billing-tier-proposal"
          />
          {enProject && (
            <ProjectTypePill
              billingTier="project"
              currentBillingTier={project.billingTier}
              label="Project"
              cost={projectCost}
              data-testid="project-details-billing-tier-project"
            />
          )}
          {enPlus && (
            <ProjectTypePill
              billingTier="projectPlus"
              currentBillingTier={project.billingTier}
              label="Project Plus"
              cost={projectCost}
              data-testid="project-details-billing-tier-project-plus"
            />
          )}
        </RadioCard>
      );
    } else if (project?.billingTier === 'project') {
      const enPlus = isBillingTierEnabled('projectPlus', subscription);
      const count = 1 + (enPlus ? 1 : 0);
      options = (
        <RadioCard
          aria-label="Select project type"
          className={colsClass[count]}
          value={watch('billingTier')}
          onValueChange={(v) => setValue('billingTier', v, { shouldDirty: true })}
        >
          <ProjectTypePill
            billingTier="project"
            currentBillingTier={project.billingTier}
            label="Project"
            data-testid="project-details-billing-tier-project"
          />
          {enPlus && (
            <ProjectTypePill
              billingTier="projectPlus"
              currentBillingTier={project.billingTier}
              label="Project Plus"
              cost={projectCost}
              data-testid="project-details-billing-tier-project-plus"
            />
          )}
        </RadioCard>
      );
    }

    if (!projectCost.value) {
      return null;
    }

    return (
      <Section
        title={
          <div className="flex w-full grow items-center justify-between font-noto">
            <Text className="grow text-base font-semibold">Select project type</Text>
            <div className="flex flex-col items-end">
              <CompareProjectTypesDialog projectCost={projectCost} testIdPrefix="project-details" className="w-5/6" />
              <CostBreakdownDialog projectCost={projectCost} testIdPrefix="project-details" className="w-[480px]" />
            </div>
          </div>
        }
      >
        {options}
      </Section>
    );
  }, [project, lengthAndArea, projectCost, currentOrganization, watch('billingTier')]);

  const projectAreaLabel = useMemo(() => {
    return `${lengthAndArea.area.value} ${lengthAndArea.area.unit}`;
  }, [lengthAndArea]);
  const projectLengthLabel = useMemo(() => {
    if (lengthAndArea.length.value === 0) {
      return null;
    }
    return `${lengthAndArea.length.value} ${lengthAndArea.length.unit}`;
  }, [lengthAndArea]);

  const onSubmit = async (data: { [key: string]: any }) => {
    const edits: { attributes: { [key: string]: any } } = {
      attributes: {},
    };

    for (const key in formState.dirtyFields) {
      edits.attributes[key] = data[key];
    }

    const updatedFields = Object.keys(edits.attributes);

    delete edits.attributes.billingTier;

    try {
      if (!project) {
        // this should never happen, but make TS happy
        return;
      }

      // Each save path below persists a different set of fields, so the tracked list is passed in.
      const trackSelfAssign = (savedFields: string[]) => {
        trackProjectManagerSelfAssigned({
          projectId: project.id,
          organizationId: project.organization || undefined,
          meId: me?.id,
          savedFields,
          savedProjectManagerId: edits.attributes.projectManagerId,
          previousProjectManagerId: project.projectManager?.id ?? null,
          viaAssignToMeButton: assignedSelfViaButtonRef.current,
        });
        assignedSelfViaButtonRef.current = false;
      };

      // The left nav, the switcher's project list and its current-project entry each hold their own
      // copy of the project, and the breadcrumb label reads the list before the entry.
      const refreshProject = () => {
        queryClient.invalidateQueries({ queryKey: ['project', { projectId }] });
        queryClient.invalidateQueries({ queryKey: ORG_SWITCHER_PROJECTS_KEY });
        queryClient.invalidateQueries({ queryKey: ORG_SWITCHER_CURRENT_PROJECT_KEY });
        refetchProject();
      };

      // check if billing tier was changed
      const isBillingTierUpdated = watch('billingTier') !== project.billingTier;

      if (isBillingTierUpdated) {
        // if yes, check if user has permissions to do it.
        if (authorizations.can(Action.CreateProject)) {
          // if yes, ask for confirmation and then update the billing tier and then update all the values
          setBillingTierConfirmationDialog(
            <UpdateBillingTierConfirmation
              toBillingTier={watch('billingTier')}
              fromBillingTier={project.billingTier}
              projectCost={projectCost}
              onDiscard={() => {
                setBillingTierConfirmationDialog(null);
              }}
              onConfirm={async () => {
                setBillingTierConfirmationDialog(<FullScreenLoading />);
                const targetTier = watch('billingTier');
                try {
                  const cost = await getCost({
                    projectType: project.geometryType!,
                    geometry: project.linear?.geometry || project.geometry,
                    subscription: currentOrganization!.subscription,
                  });

                  const totalCostDiff = (cost[targetTier].totalCost || 0) - (cost[project.billingTier].totalCost || 0);

                  const costCopy = { ...cost };
                  costCopy[targetTier].totalCost = totalCostDiff;

                  const { editsSaved, updatedProject } = await upgradeBillingTierAndSaveEdits({
                    projectId: project.id,
                    tier: targetTier,
                    costInfo: costCopy[targetTier],
                    attributes: { ...edits.attributes, billingTier: targetTier },
                  });
                  queryClient.setQueryData(['project', { projectId }], (oldData) =>
                    oldData
                      ? {
                          ...oldData,
                          ...updatedProject,
                        }
                      : updatedProject,
                  );

                  capturePosthogEvent(POSTHOG_EVENTS.projectDetailUpdated, {
                    project_id: project.id,
                    organization_id: project.organization || undefined,
                    updated_fields: updatedFields,
                  });

                  if (editsSaved) {
                    trackSelfAssign(updatedFields);
                    reset({}, { keepValues: true });
                    Notify.info({
                      title: 'Project Details',
                      message: 'Project upgraded successfully.',
                    });
                  } else {
                    // Upgrade went through but the detail edits didn't: keep the form dirty so the
                    // user can hit Save again
                    Notify.error({
                      title: 'Project upgraded, details not saved',
                      message: 'The project was upgraded, but your other changes failed to save. Please save again.',
                    });
                  }
                  refreshProject();
                } catch {
                  Notify.error({
                    title: 'Could not upgrade Project',
                    message:
                      'The upgrade did not complete and you have not been charged. Please try again or contact support.',
                  });
                } finally {
                  setBillingTierConfirmationDialog(null);
                }
              }}
            />,
          );
        } else {
          // if not, update the rest of the values, and inform they can't update
          setValue('billingTier', project.billingTier);
          const hasRemainingEdits = Object.keys(edits.attributes).length > 0;
          if (hasRemainingEdits) {
            setBillingTierConfirmationDialog(<FullScreenLoading />);
            // update project
            await apiEditProject(projectId, edits);
            capturePosthogEvent(POSTHOG_EVENTS.projectDetailUpdated, {
              project_id: project.id,
              organization_id: project.organization || undefined,
              updated_fields: Object.keys(edits.attributes),
            });
            trackSelfAssign(Object.keys(edits.attributes));
            refreshProject();
          }

          setBillingTierConfirmationDialog(null);
          Notify.error({
            title: 'Could not upgrade Project',
            message:
              'Unfortunately, you do not have enough permissions to upgrade this project. Please contact your admin.',
          });
          reset({}, { keepValues: true });
        }
      } else {
        // update project; the payload can be empty when the tier was toggled away and back
        // (dirty but unchanged) since billingTier is stripped above — skip the call then.
        if (Object.keys(edits.attributes).length > 0) {
          await apiEditProject(projectId, edits);
          trackSelfAssign(Object.keys(edits.attributes));
          refreshProject();
        }
        Notify.info({
          title: 'Project Details',
          message: 'Project details updated successfully.',
        });
        reset({}, { keepValues: true });
      }

      return;
    } catch {
      Notify.error({
        title: '⚠️',
        message: 'An error occurred while updating the project details. Please contact support.',
      });

      // if update fails, reset to original state.
      reset({});
    }
  };

  const requiredFieldsList = getRequiredFieldsForBillingTier(watch('billingTier'));

  const editGeometryButton = useMemo(() => {
    if (!project) {
      return null;
    }

    if (project.billingTier === 'draft') {
      return <EditGeometryButton project={project} />;
    } else {
      return (
        <Can action={Action.EditProjectBoundary}>
          <EditGeometryButton project={project} />
        </Can>
      );
    }
  }, [project]);

  const canAssignSelf = useMemo(() => {
    if (!me?.id || !project || !currentOrganization) return false;

    return currentOrganization.members.some((m) => m.user.id === me.id && m.status === 'active');
  }, [me, project, currentOrganization]);

  const handleAssignToMe = () => {
    if (!me?.id) return;

    setValue('projectManagerId', me.id, { shouldValidate: true, shouldDirty: true });
    assignedSelfViaButtonRef.current = true;
  };

  if (!project) {
    return <SkeletonSpinnerCentered />;
  }

  return (
    <>
      <div className="w-full">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div
            className="sticky py-1 top-0 z-50 mb-3 flex w-full justify-end space-x-2 bg-white"
            data-testid="project-details-action-bar"
          >
            {editGeometryButton}
            {project.billingTier === 'draft' && (
              <Dropdown
                side="bottom"
                align="start"
                contentTestId="project-details-print-quote-menu"
                triggerElement={
                  <Button variant="secondary" data-testid="project-details-print-quote">
                    Print Quote
                  </Button>
                }
                items={[
                  {
                    type: 'simple',
                    label: 'DOC (.docx)',
                    onClick: () => setQuoteType('docx'),
                  },
                  {
                    type: 'simple',
                    label: 'PDF (.pdf)',
                    onClick: () => setQuoteType('pdf'),
                  },
                ]}
              />
            )}
            <SendEmailModal
              open={quoteType !== null}
              onOpenChange={(open) => {
                if (!open) setQuoteType(null);
              }}
              title="Print quote"
              testIdPrefix="project-details-print-quote"
              onSend={(ccEmails) =>
                quoteType &&
                printProjectQuote(
                  projectId,
                  project.organization || undefined,
                  quoteType,
                  ccEmails.length > 0 ? ccEmails : undefined,
                )
              }
            />
            <Can action={Action.EditProjectDetails}>
              <Button disabled={isSaveDisabled || !isValid} type="submit" data-testid="project-details-save">
                Save
              </Button>
            </Can>
            <MoreButton project={project} />
          </div>
          {billingTierSelector}

          <div className="mb-40 space-y-6">
            <Section title="Project Overview" data-testid="project-details-section-overview">
              <div className="grid grid-cols-2 gap-2 gap-x-4">
                <Static label="ID" value={project?.projectId} />
                <Static label="Geometry" value={projectTypeLabel} />

                <Static label="Area" value={projectAreaLabel} />
                <Static label="Length" value={projectLengthLabel} />

                <div className="col-span-2">
                  <ControlledInput
                    label="Project Name"
                    control={control}
                    name="name"
                    isRequired={requiredFieldsList.includes('name')}
                    tooltipText={placeholderAndTooltips.projectNameTooltip}
                    data-testid="project-details-name"
                  />
                </div>
                <ControlledInput
                  label="Billing Code 1"
                  control={control}
                  name="billingCode"
                  isRequired={requiredFieldsList.includes('billingCode')}
                  tooltipText={placeholderAndTooltips.billingCodeTooltip}
                  placeholder={placeholderAndTooltips.billingCodePlaceholder}
                  data-testid="project-details-billing-code-1"
                />
                <ControlledInput
                  label="Billing Code 2"
                  control={control}
                  name="billingCode2"
                  isRequired={requiredFieldsList.includes('billingCode2')}
                  tooltipText={placeholderAndTooltips.billingCodeTooltip}
                  placeholder={placeholderAndTooltips.billingCodePlaceholder}
                  data-testid="project-details-billing-code-2"
                />
              </div>
            </Section>

            <Section title="Project Team" data-testid="project-details-section-team">
              <div className="flex flex-col gap-2">
                <FormComboBox
                  control={control}
                  name="projectManagerId"
                  label="Project Manager"
                  options={getPMs()}
                  isRequired={requiredFieldsList.includes('projectManagerId')}
                  tooltipText={placeholderAndTooltips.pmTooltip}
                  data-testid="project-details-project-manager"
                  secondaryAction={
                    canAssignSelf ? (
                      <Can action={Action.EditProjectDetails}>
                        <TextLink onClick={handleAssignToMe} data-testid="project-details-project-manager-assign-to-me">
                          Assign to me
                        </TextLink>
                      </Can>
                    ) : undefined
                  }
                />
                {programManagerEnabled && (
                  <>
                    <FormComboBox
                      control={control}
                      name="programManagerId"
                      label="Program Manager"
                      options={getPMs()}
                      isClearable
                      tooltipText={placeholderAndTooltips.programManagerTooltip}
                      data-testid="project-details-program-manager"
                    />
                    <FormComboBox
                      control={control}
                      name="subProgramManagerId"
                      label="Sub-Program Manager"
                      options={getPMs()}
                      isClearable
                      tooltipText={placeholderAndTooltips.subProgramManagerTooltip}
                      data-testid="project-details-sub-program-manager"
                    />
                  </>
                )}
                <Static
                  label="Project Team"
                  value={
                    <Link
                      to="/projects/$projectId/$tabId"
                      params={{ projectId: project.id, tabId: 'team' }}
                      className="font-noto text-xs font-normal text-blue-50 underline hover:text-blue-60"
                      data-testid="project-details-view-team"
                    >
                      View team
                    </Link>
                  }
                />
              </div>
            </Section>

            <Section
              tooltip={placeholderAndTooltips.projectNarrativeTooltip}
              title="Project Narrative"
              data-testid="project-details-section-narrative"
            >
              <div className="grid grid-cols-2 gap-2 gap-x-4">
                <ControlledInput
                  control={control}
                  name="client"
                  label="Client"
                  placeholder="City of ABC"
                  data-testid="project-details-client"
                />
                <FormComboBox
                  control={control}
                  name="type"
                  label="Project Category"
                  options={projectTypeOptions}
                  data-testid="project-details-category"
                />

                <div className="col-span-2">
                  <ControlledTextArea
                    control={control}
                    label="Scope"
                    name="scope"
                    isRequired={requiredFieldsList.includes('scope')}
                    tooltipText={placeholderAndTooltips.scopeTooltip}
                    placeholder={placeholderAndTooltips.scopePlaceholder}
                    data-testid="project-details-scope"
                  />
                </div>

                <div className="col-span-2">
                  <ControlledTextArea
                    control={control}
                    label="Project Vicinity"
                    name="projectVicinity"
                    isRequired={requiredFieldsList.includes('projectVicinity')}
                    tooltipText={placeholderAndTooltips.projectVicinityTooltip}
                    placeholder={placeholderAndTooltips.projectVicinityPlaceholder}
                    data-testid="project-details-vicinity"
                  />
                </div>

                <div className="col-span-2">
                  <ControlledInput
                    control={control}
                    name="peName"
                    label="PE/PG/PLS Name"
                    placeholder=""
                    data-testid="project-details-pe-name"
                  />
                </div>

                <ControlledInput
                  control={control}
                  name="peLicenseNumber"
                  label="PE/PG/PLS License Number"
                  placeholder=""
                  data-testid="project-details-pe-license-number"
                />
                <ControlledInput
                  control={control}
                  name="peLicenseState"
                  label="PE/PG/PLS License State"
                  placeholder=""
                  data-testid="project-details-pe-license-state"
                />
              </div>
            </Section>

            <Section title="Project Location" data-testid="project-details-section-location">
              <div className="grid grid-cols-2 gap-2 gap-x-4">
                <div className="col-span-2">
                  <ControlledInput
                    control={control}
                    label="Address"
                    name="address2.street"
                    isRequired={requiredFieldsList.includes('address2')}
                    tooltipText={placeholderAndTooltips.addressTooltip}
                    data-testid="project-details-address"
                  />
                </div>

                <div className="col-span-2">
                  <ControlledInput
                    control={control}
                    label="Cross Streets"
                    name="address2.crossStreets"
                    isRequired={requiredFieldsList.includes('address2')}
                    data-testid="project-details-cross-streets"
                  />
                </div>

                <div className="col-span-3">
                  <div className="grid grid-cols-3 gap-x-4">
                    <ControlledInput
                      control={control}
                      label="City"
                      name="address2.city"
                      isRequired={requiredFieldsList.includes('address2')}
                      data-testid="project-details-city"
                    />
                    <ControlledInput
                      control={control}
                      label="State"
                      name="address2.state"
                      isRequired={requiredFieldsList.includes('address2')}
                      data-testid="project-details-state"
                    />
                    <ControlledInput
                      control={control}
                      label="ZIP Code"
                      name="address2.zipCode"
                      isRequired={requiredFieldsList.includes('address2')}
                      data-testid="project-details-zip-code"
                    />
                  </div>
                </div>
              </div>
            </Section>

            <Section title="Project Timelines" data-testid="project-details-section-timelines">
              <div className="grid grid-cols-2 gap-2 gap-x-4">
                <ControlledDatePicker
                  control={control}
                  name="startDate"
                  label="Start Date"
                  data-testid="project-details-start-date"
                />
                <ControlledDatePicker
                  control={control}
                  name="endDate"
                  label="End Date"
                  minDate={watch('startDate')}
                  data-testid="project-details-end-date"
                />

                <ControlledDatePicker
                  control={control}
                  name="constructionStartDate"
                  label="Construction Start Date"
                  minDate={startDate}
                  data-testid="project-details-construction-start-date"
                />
                <ControlledDatePicker
                  control={control}
                  name="constructionEndDate"
                  label="Construction End Date"
                  minDate={concStart}
                  data-testid="project-details-construction-end-date"
                  validate={(value, formValues) => {
                    const start = formValues.constructionStartDate;
                    if (value && start && value < start) {
                      return 'Construction end date must be after construction start date.';
                    } else {
                      return true;
                    }
                  }}
                />
                <Static label="Created On" value={project.createdAt.toLocaleString(DateTime.DATE_MED)} />
              </div>
            </Section>
          </div>
        </Form>
      </div>
      <MapRequestsAlert />
      {billingTierConfirmationDialog}
    </>
  );
}
